import { useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import { getAuthToken } from "../utils/apiUtils";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

/**
 * Custom hook for @mention autocomplete functionality
 * 
 * Usage:
 *   const { suggestions, showDropdown, cursorPosition, handleInputChange, insertMention, resetMentions } = useMentionAutocomplete();
 *   
 *   <textarea 
 *     value={content} 
 *     onChange={(e) => { setContent(e.target.value); handleInputChange(e.target.value, e.target.selectionStart); }}
 *   />
 *   {showDropdown && <MentionDropdown suggestions={suggestions} onSelect={insertMention} />}
 */
const useMentionAutocomplete = (options = {}) => {
  const { minQueryLength = 2, debounceMs = 300 } = options;
  
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentQuery, setCurrentQuery] = useState("");
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  
  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);
  
  // Fetch mention suggestions from API
  const fetchSuggestions = useCallback(async (query) => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    
    try {
      setLoading(true);
      const token = getAuthToken(); // Use centralized token retrieval
      
      const response = await axios.get(
        `${API_BASE_URL}/posts/mentions/search`,
        {
          params: { q: query },
          headers: { Authorization: `Bearer ${token}` },
          signal: abortControllerRef.current.signal,
        }
      );
      
      if (response.data.success) {
        setSuggestions(response.data.users || []);
      }
    } catch (error) {
      if (error.name !== "CanceledError") {
        setSuggestions([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Handle text input changes - detect @ mentions
  const handleInputChange = useCallback((text, cursorPosition) => {
    // Clear any pending debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Find the last @ before cursor position
    const textBeforeCursor = text.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf("@");
    
    if (lastAtIndex === -1) {
      setShowDropdown(false);
      setSuggestions([]);
      setCurrentQuery("");
      return;
    }
    
    // Extract query after @
    const query = textBeforeCursor.substring(lastAtIndex + 1);
    
    // Check if query contains space (mention ended) or is too short
    if (query.includes(" ") || query.includes("\n")) {
      setShowDropdown(false);
      setSuggestions([]);
      setCurrentQuery("");
      return;
    }
    
    setMentionStartIndex(lastAtIndex);
    setCurrentQuery(query);
    
    // Only search if query meets minimum length
    if (query.length >= minQueryLength) {
      setShowDropdown(true);
      
      // Debounced API call
      debounceTimerRef.current = setTimeout(() => {
        fetchSuggestions(query);
      }, debounceMs);
    } else {
      setShowDropdown(false);
      setSuggestions([]);
    }
  }, [minQueryLength, debounceMs, fetchSuggestions]);
  
  // Insert selected mention into text
  const insertMention = useCallback((user, currentText, cursorPosition) => {
    if (mentionStartIndex === -1) return currentText;
    
    const textBefore = currentText.substring(0, mentionStartIndex);
    const textAfter = currentText.substring(cursorPosition);
    
    // Insert @Name with a space after
    const newText = textBefore + "@" + user.name + " " + textAfter;
    
    // Reset state
    setShowDropdown(false);
    setSuggestions([]);
    setCurrentQuery("");
    setMentionStartIndex(-1);
    
    // Return new text and new cursor position
    return {
      text: newText,
      cursorPosition: mentionStartIndex + user.name.length + 2, // +2 for @ and space
    };
  }, [mentionStartIndex]);
  
  // Reset all mention state
  const resetMentions = useCallback(() => {
    setShowDropdown(false);
    setSuggestions([]);
    setCurrentQuery("");
    setMentionStartIndex(-1);
    setLoading(false);
  }, []);
  
  // Close dropdown
  const closeDropdown = useCallback(() => {
    setShowDropdown(false);
  }, []);
  
  return {
    suggestions,
    showDropdown,
    loading,
    currentQuery,
    handleInputChange,
    insertMention,
    resetMentions,
    closeDropdown,
  };
};

export default useMentionAutocomplete;
